// Difficulty modes — bias the target-level distribution around the user's
// estimated CEFR level. Each bucket is { off, weight }. Weights must sum
// to 1. `adaptive` is the default; `easy` biases toward easier review;
// `challenge` biases toward harder cards.

export type Bucket = { off: number; weight: number };
export type DifficultyMode = { short: string; buckets: Bucket[] };
export type DifficultyKey = "easy" | "adaptive" | "challenge";

export const DIFFICULTY_MODES: Record<DifficultyKey, DifficultyMode> = {
  easy: {
    // Arrow-based glyphs rather than A-/A/A+: the latter collided
    // visually with the CEFR levels (A1-C2) displayed elsewhere. ↓/=/↑
    // conveys "bias below / at / above your estimated level" directly.
    short: "↓",
    buckets: [
      { off: -2, weight: 0.15 },
      { off: -1, weight: 0.35 },
      { off: 0, weight: 0.4 },
      { off: 1, weight: 0.1 },
      { off: 2, weight: 0 },
    ],
  },
  adaptive: {
    short: "=",
    buckets: [
      { off: -2, weight: 0.05 },
      { off: -1, weight: 0.1 },
      { off: 0, weight: 0.55 },
      { off: 1, weight: 0.25 },
      { off: 2, weight: 0.05 },
    ],
  },
  challenge: {
    short: "↑",
    buckets: [
      { off: -2, weight: 0 },
      { off: -1, weight: 0.05 },
      { off: 0, weight: 0.3 },
      { off: 1, weight: 0.45 },
      { off: 2, weight: 0.2 },
    ],
  },
};

export const DIFFICULTY_ORDER: DifficultyKey[] = ["easy", "adaptive", "challenge"];

// Game modes — "libre" is the default (endless), "defi" runs a fixed
// batch of 50 cards then shows a score recap, "revision" only picks
// cards that are due (Leitner dueAt <= now).
export type GameModeKey = "libre" | "defi" | "revision";
export type GameMode = { icon: string; count: number };

export const GAME_MODES: Record<GameModeKey, GameMode> = {
  libre:    { icon: "∞",  count: Infinity },
  defi:     { icon: "50", count: 50 },
  revision: { icon: "↺",  count: Infinity },
};

export const GAME_MODE_ORDER: GameModeKey[] = ["libre", "defi", "revision"];
